import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { dateTimeStrToDateStr } from "@/utils/utils";

import type { DailyMonitoringModel } from "./daily-monitoring-model";

/**
 * Last-measurement cell for a not-completed row. Lazily loads the patient's
 * `last_ppg` on mount and shows its date — how long a patient has been silent
 * is what ranks them once you know they haven't measured today.
 *
 * Same states as the other lazy cells: a placeholder while loading, a dash on
 * failure, and a dash for a patient who has never measured.
 */
export const MonitoringLastMeasuredCell = observer(
  function MonitoringLastMeasuredCell({
    model,
    patientId,
  }: {
    model: DailyMonitoringModel;
    patientId: number;
  }) {
    useEffect(() => {
      void model.ensureLastMeasured(patientId);
    }, [model, patientId]);

    const testId = `dm-last-measured-${patientId}`;
    const entry = model.lastMeasured.get(patientId);

    if (!entry || entry.status === "loading") {
      return <span className="text-muted-foreground">…</span>;
    }
    if (entry.status === "error" || entry.lastPpg == null) {
      return (
        <span data-testid={testId} className="text-muted-foreground">
          –
        </span>
      );
    }
    return (
      <span data-testid={testId}>{dateTimeStrToDateStr(entry.lastPpg)}</span>
    );
  },
);
